import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { remittanceService } from '../services/remittance';
import { RemittanceFilter } from '../types/remittance';

export const remittanceKeys = {
  all: ['remittance'] as const,
  files: (filter: RemittanceFilter) => [...remittanceKeys.all, 'files', filter] as const,
  list: (userId: number | string, params: any) => [...remittanceKeys.all, 'list', userId, params] as const,
  detail: (userId: number | string, remittanceId: string, params: any) => [...remittanceKeys.all, 'detail', userId, remittanceId, params] as const,
};

export const useRemittanceFiles = (filter: RemittanceFilter) => {
  return useQuery({
    queryKey: remittanceKeys.files(filter),
    queryFn: () => remittanceService.getRemittanceFiles(filter),
    enabled: !!filter.fromDate && !!filter.toDate,
  });
};

export const useUploadInwardRemittance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      remittanceService.uploadInwardRemittance(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: remittanceKeys.all });
    },
  });
};

export const useUploadOutwardRemittance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      remittanceService.uploadOutwardRemittance(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: remittanceKeys.all });
    },
  });
};

export const useExportUpcoming = () => {
  return useMutation({
    mutationFn: (userId: number) => remittanceService.getExportUpcomingData(userId),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `outward-remittance-upcoming-${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
};

export const useExportProcessMaster = () => {
  return useMutation({
    mutationFn: (userId: number) => remittanceService.getExportProcessMasterData(userId),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `remittance-process-master-${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
};

export const useRemittanceList = (
  userId: number | string | undefined, 
  params: { page?: number; size?: number; search?: string; status?: string; fromDate?: string; toDate?: string }
) => {
  return useQuery({
    queryKey: remittanceKeys.list(userId!, params),
    queryFn: () => remittanceService.getRemittanceList(userId!, params),
    enabled: !!userId,
  });
};

export const useRemittanceDetail = (userId: number | string | undefined, remittanceId: string | undefined, params: { searchTracking?: string }) => {
  return useQuery({
    queryKey: remittanceKeys.detail(userId!, remittanceId!, params),
    queryFn: () => remittanceService.getRemittanceDetail(userId!, remittanceId!, params),
    enabled: !!userId && !!remittanceId,
  });
};

export const useExportLedgers = () => {
  return useMutation({
    mutationFn: ({ userId, params }: { userId: number | string; params: any }) =>
      remittanceService.getExportLedgersData(userId, params),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const formattedDate = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `remittance_ledgers_${formattedDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
};

export const useExportLedgerDetail = () => {
  return useMutation({
    mutationFn: ({ userId, ledgerNumber, params }: { userId: number | string; ledgerNumber: string; params: any }) =>
      remittanceService.getExportLedgerDetailData(userId, ledgerNumber, params),
    onSuccess: (blob, variables) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const formattedDate = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `remittance_detail_${variables.ledgerNumber}_${formattedDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
};

export const useExportMultipleLedgersZip = () => {
  return useMutation({
    mutationFn: ({ userId, ledgerNumbers }: { userId: number | string; ledgerNumbers: string[] }) =>
      remittanceService.getExportMultipleLedgersZip(userId, ledgerNumbers),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const formattedDate = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `remittances_${formattedDate}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
  });
};
